import { API_BASE_URL, CUSTOM_FONTS_DIR, STORAGE_URL } from '@/utils/const';

/* -------------------------------------------------------------------------
   Custom fonts — the browser-side half.

   A custom font is a file in the fonts bucket plus a CSS family name. The
   built-in Hebrew faces are declared with @font-face in assets/style/index.css,
   which only works for files that ship with the build; an uploaded face has to
   be registered at runtime instead, which is what `registerCustomFont` does
   through the FontFace API. Once registered the family behaves exactly like a
   built-in one — `font-family: 'MyFace'` resolves anywhere in the document.

   Registration is idempotent and cached by family name, so calling it again
   for a face that is already loaded is free.
   ------------------------------------------------------------------------- */

export interface CustomFontRecord {
  id?: string;
  name?: string;
  family?: string;
  file?: string;
  fileSize?: number;
  createdAt?: string;
}

/** Extensions the picker accepts, in the order they are offered to the user. */
export const CUSTOM_FONT_EXTENSIONS = ['.woff2', '.woff', '.otf', '.ttf'];

/** `accept` attribute for the file input. */
export const CUSTOM_FONT_ACCEPT = CUSTOM_FONT_EXTENSIONS.join(',');

/** Upload ceiling. Web fonts above this are almost always unsubsetted. */
export const CUSTOM_FONT_MAX_BYTES = 5 * 1024 * 1024;

const FORMAT_BY_EXTENSION: Record<string, string> = {
  '.woff2': 'woff2',
  '.woff': 'woff',
  '.otf': 'opentype',
  '.ttf': 'truetype',
};

/** Lower-cased extension of a filename, including the dot ('' when absent). */
export const fileExtension = (fileName: string): string => {
  const match = /\.[^.]+$/.exec(String(fileName || ''));
  return match ? match[0].toLowerCase() : '';
};

/** The CSS `format()` hint for a font file, or '' for an unknown extension. */
export const fontFormat = (fileName: string): string =>
  FORMAT_BY_EXTENSION[fileExtension(fileName)] || '';

/** Public URL of a stored font file, straight off the storage bucket. */
export const customFontUrl = (file: string): string =>
  `${STORAGE_URL}/${CUSTOM_FONTS_DIR}/${file}`;

/**
 * Same-origin URL for a stored font file, served through the API.
 *
 * Unlike an <img>, a web font is fetched with CORS, so a face loaded straight
 * off the bucket only renders where that bucket carries a CORS policy for this
 * origin. The API is already CORS-configured for the app, so this is what
 * `registerCustomFont` retries through when the direct URL will not load.
 */
export const customFontProxyUrl = (file: string): string =>
  `${API_BASE_URL}/customFont/file/${encodeURIComponent(file)}`;

/** Where a stored font file may be loaded from, fastest source first. */
export const customFontSources = (file: string): string[] => [
  customFontUrl(file),
  customFontProxyUrl(file),
];

/**
 * Whether a family token is safe to drop into a CSS selector, a class name and
 * a font stack unquoted. `toFontFamilyToken` only ever produces such tokens; a
 * row that somehow holds anything else is skipped rather than rendered.
 */
export const isSafeFontFamily = (family: unknown): boolean =>
  /^[A-Za-z][A-Za-z0-9]*$/.test(String(family || ''));

/** Human-readable file size, e.g. "412 KB". */
export const formatFileSize = (bytes: number): string => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Turn a human label into a CSS-safe family token: "Shefa Display 2" becomes
 * "ShefaDisplay2". Designs store this token, so it has to stay stable and free
 * of characters that would need quoting inside a font stack.
 */
export const toFontFamilyToken = (label: string): string => {
  const cleaned = String(label || '')
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
  // A family may not start with a digit — CSS would read it as a number.
  return /^\d/.test(cleaned) ? `Font${cleaned}` : cleaned;
};

/**
 * Why a file cannot be uploaded, or null when it is fine. Mirrors the checks
 * the upload route should repeat server-side.
 */
export const customFontFileError = (file: File | null): string | null => {
  if (!file) return 'Choose a font file to upload.';
  if (!CUSTOM_FONT_EXTENSIONS.includes(fileExtension(file.name))) {
    return `Unsupported file type. Use ${CUSTOM_FONT_EXTENSIONS.join(', ')}.`;
  }
  if (file.size > CUSTOM_FONT_MAX_BYTES) {
    return `Font is ${formatFileSize(file.size)} — the limit is ${formatFileSize(
      CUSTOM_FONT_MAX_BYTES
    )}.`;
  }
  return null;
};

/* ----------------------------- registration ------------------------------ */

// Families already handed to the document, so a re-render never re-downloads
// a face. Values are the in-flight/settled load promise.
const registered = new Map<string, Promise<boolean>>();

/** Load one face from one URL and add it to the document. */
const loadFontFace = (family: string, url: string): Promise<boolean> => {
  const FontFaceCtor = (window as any).FontFace;

  return new FontFaceCtor(family, `url(${JSON.stringify(url)})`)
    .load()
    .then((loaded: any) => {
      (document as any).fonts.add(loaded);
      return true;
    })
    .catch(() => false);
};

/**
 * Load `family` from its stored file and add it to the document so
 * `font-family: family` resolves. Every source in `customFontSources` is tried
 * in turn, so a bucket without a CORS policy falls back to the API. Resolves
 * false when no source yields a usable font — callers render a "could not load"
 * state rather than showing a silent fallback face.
 */
export const registerCustomFont = (family: string, file: string): Promise<boolean> => {
  if (!family || !file) return Promise.resolve(false);
  if (registered.has(family)) return registered.get(family) as Promise<boolean>;

  // Environments without the FontFace API (very old browsers) simply keep the
  // fallback stack; nothing else in the app depends on the load succeeding.
  const FontFaceCtor = (window as any).FontFace;
  if (!FontFaceCtor || !(document as any).fonts) {
    return Promise.resolve(false);
  }

  const loading = customFontSources(file)
    .reduce(
      (attempt, url) => attempt.then((ok) => (ok ? true : loadFontFace(family, url))),
      Promise.resolve(false)
    )
    .then((ok) => {
      // Drop the failed entry so a later retry (re-upload, network back) can
      // load the same family again.
      if (!ok) registered.delete(family);
      return ok;
    });

  registered.set(family, loading);
  return loading;
};

/** Register every stored font in one pass; resolves when all have settled. */
export const registerCustomFonts = (
  fonts: CustomFontRecord[]
): Promise<boolean[]> =>
  Promise.all(
    (fonts || [])
      .filter((font) => font?.family && font?.file)
      .map((font) => registerCustomFont(String(font.family), String(font.file)))
  );

/** Forget a family so it can be registered again from a different file. */
export const forgetCustomFont = (family: string): void => {
  registered.delete(family);
};

/**
 * Register a face straight from a local File for pre-upload preview. The blob
 * URL is revoked once the browser has parsed the file.
 */
export const previewCustomFontFile = async (
  family: string,
  file: File
): Promise<boolean> => {
  const FontFaceCtor = (window as any).FontFace;
  if (!FontFaceCtor || !(document as any).fonts || !file) return false;

  const objectUrl = URL.createObjectURL(file);
  try {
    const loaded = await new FontFaceCtor(family, `url(${JSON.stringify(objectUrl)})`).load();
    (document as any).fonts.add(loaded);
    return true;
  } catch (e) {
    return false;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/* ------------------------------ Quill styles ------------------------------ */

// Quill addresses a font by class (`ql-font-<family>`) rather than by inline
// style, and takes its picker labels from CSS `content`. Both are declared up
// front in assets/style/index.css for the built-in faces; an uploaded face is
// only known at runtime, so its rules are written into this one stylesheet.
// It is also what makes saved Quill content render in the right face wherever
// it is displayed, not just inside the editor.
const QUILL_FONT_STYLE_ELEMENT_ID = 'sk-custom-quill-fonts';

export const ensureQuillCustomFontStyles = (fonts: CustomFontRecord[]): void => {
  if (typeof document === 'undefined') return;

  const css = (fonts || [])
    .filter((font) => isSafeFontFamily(font?.family))
    .map((font) => {
      const family = String(font.family);
      // The label only ever lands inside a CSS string literal, so the two
      // characters that could break out of one are dropped.
      const label = String(font.name || family).replace(/['\\]/g, '');
      return [
        `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value='${family}']::before,`,
        `.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='${family}']::before {`,
        `  content: '${label}';`,
        `  font-family: '${family}';`,
        '}',
        `.ql-font-${family} { font-family: '${family}'; }`,
      ].join('\n');
    })
    .join('\n');

  let style = document.getElementById(QUILL_FONT_STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (!css) {
    if (style) style.textContent = '';
    return;
  }

  if (!style) {
    style = document.createElement('style');
    style.id = QUILL_FONT_STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }

  style.textContent = css;
};

/**
 * Make a freshly fetched font library usable: hand every face to the browser
 * and refresh the Quill rules. Called from the store the moment the list lands,
 * so no caller has to remember to do it.
 */
export const applyCustomFonts = (fonts: CustomFontRecord[]): void => {
  ensureQuillCustomFontStyles(fonts);
  registerCustomFonts(fonts);
};

/* ------------------------------ font pickers ------------------------------ */

/** The organization's uploaded fonts as currently held in the vuex store. */
export const storedCustomFonts = (store: any): CustomFontRecord[] =>
  Array.isArray(store?.getters?.customFonts?.data) ? store.getters.customFonts.data : [];

/** Font-picker options for the fonts an organization has uploaded. */
export const customFontOptions = (
  fonts: CustomFontRecord[]
): Array<{ label: string; value: string }> =>
  (fonts || [])
    .filter((font) => isSafeFontFamily(font?.family))
    .map((font) => ({
      label: String(font.name || font.family),
      value: String(font.family),
    }));

interface FontOption {
  label: string;
  value: string;
}

// Built-ins first, uploads after, and never the same value twice — a custom
// family that collides with a built-in would otherwise show up as a duplicate
// entry selecting the same face.
const appendUniqueOptions = (builtIn: FontOption[], custom: FontOption[]): FontOption[] => {
  const seen = new Set((builtIn || []).map((option) => option.value));
  return [...(builtIn || []), ...custom.filter((option) => !seen.has(option.value))];
};

/**
 * Built-in + uploaded fonts as `{ label, value }`, where the value is a bare
 * family name — the shape the minyanim / zmanim design dropdowns store.
 */
export const withCustomFontFamilyOptions = (
  builtIn: FontOption[],
  fonts: CustomFontRecord[]
): FontOption[] => appendUniqueOptions(builtIn, customFontOptions(fonts));

/**
 * Built-in + uploaded fonts as `{ label, value }`, where the value is a full
 * CSS font stack — the shape the tiptap editors write into `font-family`.
 */
export const withCustomFontStackOptions = (
  builtIn: FontOption[],
  fonts: CustomFontRecord[]
): FontOption[] =>
  appendUniqueOptions(
    builtIn,
    customFontOptions(fonts).map((option) => ({
      label: option.label,
      value: `${option.value}, sans-serif`,
    }))
  );

/**
 * Built-in + uploaded fonts as bare family strings — the shape a Quill font
 * whitelist and the zmanim design dropdown take.
 */
export const withCustomFontNames = (
  builtIn: string[],
  fonts: CustomFontRecord[]
): string[] => {
  const seen = new Set(builtIn || []);
  return [
    ...(builtIn || []),
    ...(fonts || [])
      .filter((font) => isSafeFontFamily(font?.family) && !seen.has(String(font.family)))
      .map((font) => String(font.family)),
  ];
};
